import type { UseQueryOptions } from "@tanstack/react-query";
import {
  queryOptions,
  skipToken,
  useQuery,
  useQueryClient
} from "@tanstack/react-query";
import { apiWithoutCamelization } from "../api";
import type {
  GeneratorListItemResponse,
  GeneratorSingleEndpointResponse
} from "../components/ObjectModals/GeneratorModal/GeneratorModal.types";
import urls from "../urls";
import { generatorsQueries } from "./useGenerators";
import { fetchGeneratorSingleEndpointList } from "./useGeneratorSingleEndpointList";

export const generatorSingleEndpointQueries = {
  all: () =>
    queryOptions<GeneratorSingleEndpointResponse>({
      queryKey: ["generatorSingleEndpoint"]
    }),
  list: ({
    siteId,
    vermarktungsvertragCompanyId
  }: {
    siteId?: number;
    vermarktungsvertragCompanyId?: number;
  }) =>
    queryOptions<Array<GeneratorListItemResponse>>({
      queryKey: [
        "generatorSingleEndpoint",
        { siteId, vermarktungsvertragCompanyId }
      ],
      queryFn:
        siteId || vermarktungsvertragCompanyId
          ? () =>
              fetchGeneratorSingleEndpointList({
                siteId,
                vermarktungsvertragCompanyId
              })
          : skipToken,
      staleTime: 1000 * 60 * 5 // 5 minutes
    }),
  detail: (generatorId?: number) =>
    queryOptions<GeneratorSingleEndpointResponse>({
      queryKey: ["generatorSingleEndpoint", { generatorId }],
      queryFn: generatorId
        ? () => fetchGeneratorSingleEndpoint(generatorId)
        : skipToken
    })
};

export function useGeneratorSingleEndpoint(
  generatorId?: number,
  options?: Partial<UseQueryOptions<GeneratorSingleEndpointResponse>>,
  siteId?: number
) {
  const queryClient = useQueryClient();
  return useQuery({
    ...generatorSingleEndpointQueries.detail(generatorId),
    placeholderData: () => {
      const generator = queryClient
        .getQueryData(generatorsQueries.list({ siteId }).queryKey)
        ?.find((g) => g.id === generatorId);
      return {
        name: generator?.name
      };
    },
    ...options
  });
}

export function usePrefetchGeneratorSingleEndpoint() {
  const queryClient = useQueryClient();

  return (generatorId: number) => {
    return queryClient.prefetchQuery({
      ...generatorSingleEndpointQueries.detail(generatorId),
      staleTime: 1000 * 60 // 1 minute
    });
  };
}

async function fetchGeneratorSingleEndpoint(generatorId: number) {
  const response =
    await apiWithoutCamelization.get<GeneratorSingleEndpointResponse>(
      urls.apiWithoutCamelization.generatorSingleEndpoint(generatorId)
    );
  return response.data;
}
